import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, productCreatorStagePath } from "@travily/routes";
import type ListingsCreatorSellingCountry from "@travily/types/pages/ListingsCreatorSellingCountry";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useSellingCountryStage } from "@/lib/wizard/use-selling-country-stage";
import { ProductSellingCountryStepView } from "./show.view";

type PageProps = ListingsCreatorSellingCountry;

// Asked once for a paid seller who has not told us where they get paid; sets the
// account country -> currency, framed as banking/payout, never taxes. The confirm-
// before-reprice flow lives in useSellingCountryStage (shared with services).
const ProductSellingCountryStep = ({
  listing_id,
  stage_index,
  stage_order,
  country,
  current_currency,
  repriceable_count,
  supported_countries,
}: PageProps) => {
  const {
    form,
    fieldError,
    currentStage,
    totalStages,
    onBack,
    primaryPending,
    selected,
    confirmOpen,
    handleContinue,
    handleConfirm,
    handleConfirmOpenChange,
  } = useSellingCountryStage({
    stageUrl: (stage) => productCreatorStagePath(listing_id, stage),
    stageIndex: stage_index,
    stageOrder: stage_order,
    country,
    currentCurrency: current_currency,
    repriceableCount: repriceable_count,
    supportedCountries: supported_countries,
  });

  const countryError = fieldError("country");
  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.products.creator.steps.selling_country.show.meta_title")}>
        <meta name="description" content={t("web.products.creator.steps.selling_country.show.meta_description")} />
      </Head>
      <ProductSellingCountryStepView
        currentStage={currentStage}
        totalStages={totalStages}
        onBack={onBack}
        topBarActions={topBarActions}
        onPrimary={handleContinue}
        primaryPending={primaryPending}
        countryError={countryError}
        country={form.data.country}
        onCountryChange={(value) => form.setData("country", value)}
        supportedCountries={supported_countries}
        selectedCurrency={selected?.currency}
        currentCurrency={current_currency}
        repriceableCount={repriceable_count}
        currencyConfirmOpen={confirmOpen}
        onCurrencyConfirmOpenChange={handleConfirmOpenChange}
        onCurrencyConfirm={handleConfirm}
      />
    </>
  );
};

export default ProductSellingCountryStep;
